import { Box, Button, Container, FormControl, TextField, Typography } from '@mui/material'
import { Controller, useForm } from 'react-hook-form'
import { useContext, useEffect, useState } from 'react'
import { HotelContext } from '../contexts/HotelContext'
import { useSnackbar } from 'notistack'
import { useLocation, useNavigate } from 'react-router'
import qs from 'qs'
import { OverlayLoader } from '../components/Loader'
import { Password } from '../components/Password'
import SearchIcon from '@mui/icons-material/Search'
import { reservationLogin } from '../apis/checkinApis'
import { useConfirm } from 'material-ui-confirm'
import { RatingDialog } from '../modules/RatingDialog'
import { environment } from '../environments/environment'
import { BackButton } from '../components/BackButton'

interface IReservationLoginForm {
  phoneNumber: string
  reservationNumber: string
}

interface IReservationLoginQuery {
  redirectUri?: string
}

export const ReservationLogin = () => {
  const { hotel, setReservationToken, clearReservationToken } = useContext(HotelContext)
  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
  } = useForm({
    defaultValues: {
      phoneNumber: '',
      reservationNumber: '',
    },
  })
  const [isRatingOpen, setIsRatingOpen] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const location = useLocation()
  const confirm = useConfirm()
  const query = qs.parse(location.search, { ignoreQueryPrefix: true }) as IReservationLoginQuery
  const redirectUri = query?.redirectUri || `/hotel/${hotel.hotelId}/checkin`

  useEffect(() => {
    if (environment.kiosk) {
      clearReservationToken()
    }
  }, [])

  const onSubmit = async (values: IReservationLoginForm) => {
    try {
      const { token } = await reservationLogin(hotel.hotelId, {
        phoneNumber: values.phoneNumber,
        reservationNumber: values.reservationNumber,
      })
      await setReservationToken(token)
      navigate(redirectUri)
    } catch (err) {
      await enqueueSnackbar({
        variant: 'error',
        message: 'Không tìm thấy thông tin đặt phòng, vui lòng thử lại',
      })
    }
  }

  const handleContact = async () => {
    try {
      await confirm({
        description: `Quý khách vui lòng liên hệ tại quầy hoặc SĐT ${hotel.phoneNumber} nhé!`,
        title: 'Liên hệ lễ tân',
      })
    } catch (error) {
      return
    }
  }

  const handleRating = async () => {
    setIsRatingOpen(true)
  }

  return (
    <Container>
      {!environment.kiosk && <BackButton to={`/`} />}
      {isSubmitting && <OverlayLoader />}
      <Box>
        <Typography
          color={(theme) => theme.palette.primary.main}
          textAlign="center"
          variant="h3"
          mt={4}
          fontWeight="600">
          Nhận phòng
          <Typography mt={1} color={(theme) => theme.palette.grey[600]} textAlign="center">
            Nhập số điện thoại và mã đặt phòng để tìm kiếm
          </Typography>
        </Typography>
      </Box>
      <Container maxWidth="sm">
        <Box display="flex" alignItems="center" flexDirection="column" mt={3}>
          <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
            <Controller
              control={control}
              name="reservationNumber"
              rules={{
                required: true,
              }}
              render={({ field }) => (
                <FormControl fullWidth>
                  <TextField
                    {...field}
                    autoComplete="off"
                    inputProps={{
                      'aria-autocomplete': 'none',
                    }}
                    margin="normal"
                    error={Boolean(errors.reservationNumber)}
                    fullWidth
                    type="number"
                    label="Mã đặt phòng"
                  />
                </FormControl>
              )}
            />
            <Controller
              control={control}
              name="phoneNumber"
              rules={{
                required: true,
              }}
              render={({ field }) => (
                <FormControl fullWidth>
                  <Password
                    {...field}
                    autoComplete="off"
                    inputProps={{
                      'aria-autocomplete': 'none',
                    }}
                    type="number"
                    margin="normal"
                    error={Boolean(errors.phoneNumber)}
                    fullWidth
                    label="Số điện thoại"
                  />
                </FormControl>
              )}
            />
            <Box display="flex" textAlign="center" justifyContent="center" gap={1} mt={4}>
              <Button fullWidth size="large" type="submit" variant="contained">
                <SearchIcon />
                Tìm kiếm
              </Button>
            </Box>
          </form>
        </Box>
        <Box display="flex" textAlign="center" justifyContent="space-between" gap={1} mt={4}>
          {
            <Button
              size="large"
              fullWidth
              variant="outlined"
              onClick={() => navigate(`/hotel/${hotel.hotelId}/search`)}>
              Đặt phòng
            </Button>
          }
          <Button size="large" fullWidth variant="outlined" onClick={handleContact}>
            Liên hệ lễ tân
          </Button>
          <Button size="large" fullWidth variant="outlined" onClick={handleRating}>
            Đánh giá
          </Button>
        </Box>
        {isRatingOpen && <RatingDialog open={isRatingOpen} onClose={() => setIsRatingOpen(false)} />}
      </Container>
    </Container>
  )
}
